import { useRef, useState } from "react";
import { Play, Pause, RotateCcw, Maximize, Trophy, X } from "lucide-react";
import AppShell from "@/components/AppShell";
import useCountdown from "@/lib/useCountdown";

const TIMES = [1, 2, 3, 5, 10];
const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const EMPTY_ATHLETE = () => ({ name: "", points: 0, adv: 0, pen: 0 });

const btnCls =
  "font-display font-bold rounded-xl border border-white/15 bg-white/5 hover:border-gold/60 hover:text-gold transition-colors py-3 text-lg";

export default function Placar() {
  const { duration, remaining, running, setDuration, start, pause, reset } = useCountdown(300);
  const [athletes, setAthletes] = useState([EMPTY_ATHLETE(), EMPTY_ATHLETE()]);
  const [winner, setWinner] = useState(null);
  const boardRef = useRef(null);

  const update = (idx, field, delta, absolute = null) => {
    setAthletes((prev) =>
      prev.map((a, i) =>
        i === idx ? { ...a, [field]: absolute !== null ? absolute : Math.max(0, a[field] + delta) } : a
      )
    );
  };

  const resetAll = () => {
    reset();
    setAthletes([EMPTY_ATHLETE(), EMPTY_ATHLETE()]);
    setWinner(null);
  };

  const finish = (idx) => {
    pause();
    setWinner(idx);
  };

  const fullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else boardRef.current?.requestFullscreen?.();
  };

  const sides = [
    { idx: 0, label: "ATLETA 1", accent: "text-gold", border: "border-gold/40", bg: "bg-gold/5" },
    { idx: 1, label: "ATLETA 2", accent: "text-crimson", border: "border-crimson/40", bg: "bg-crimson/5" },
  ];

  return (
    <AppShell active="material">
      <div ref={boardRef} className="relative bg-obsidian rounded-2xl overflow-hidden" data-testid="placar-board">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img src="/assets/logo.jpeg" alt="" className="w-[55%] max-w-lg opacity-[0.07] rounded-full" />
        </div>

        <div className="relative p-5 sm:p-8">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-display text-xl font-bold" data-testid="placar-title">Placar</h1>
              <p className="font-mono text-[10px] tracking-[0.3em] text-gold mt-1">CAMPEONATO INTERNO · JIU-JITSU</p>
            </div>
            <button type="button" onClick={fullscreen} data-testid="placar-fullscreen" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-widest border border-white/15 text-zinc-300 px-4 py-2.5 rounded-full hover:border-gold/50 hover:text-gold transition-colors">
              <Maximize size={14} /> TELA CHEIA (TV)
            </button>
          </div>

          <div className="mt-6 text-center">
            <p className={`font-mono font-bold text-7xl sm:text-8xl tracking-wider ${remaining <= 10 && remaining > 0 ? "text-crimson" : "text-[#F5F5F0]"}`} data-testid="placar-timer">
              {fmt(remaining)}
            </p>
            <div className="mt-4 flex items-center justify-center gap-2 flex-wrap" data-testid="placar-times">
              {TIMES.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setDuration(m * 60)}
                  data-testid={`placar-time-${m}`}
                  className={`font-mono text-xs tracking-wider px-4 py-2 rounded-full border transition-colors ${duration === m * 60 ? "border-gold bg-gold/15 text-gold" : "border-white/15 text-zinc-500 hover:border-gold/40"}`}
                >
                  {m} MIN
                </button>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={running ? pause : start}
                data-testid="placar-start-pause"
                className="inline-flex items-center gap-2 font-display font-semibold bg-gold text-obsidian px-8 py-3.5 rounded-full hover:bg-gold-amber transition-colors"
              >
                {running ? <Pause size={18} /> : <Play size={18} />} {running ? "Pausar" : "Iniciar"}
              </button>
              <button type="button" onClick={resetAll} data-testid="placar-reset" className="inline-flex items-center gap-2 font-mono text-xs tracking-widest border border-white/15 text-zinc-300 px-6 py-3.5 rounded-full hover:border-crimson/60 hover:text-crimson transition-colors">
                <RotateCcw size={14} /> ZERAR
              </button>
            </div>
          </div>

          <div className="mt-8 grid md:grid-cols-2 gap-5">
            {sides.map(({ idx, label, accent, border, bg }) => {
              const a = athletes[idx];
              return (
                <div key={idx} className={`rounded-2xl border ${border} ${bg} p-5`} data-testid={`placar-atleta-${idx}`}>
                  <p className={`font-mono text-[10px] tracking-[0.3em] ${accent}`}>{label}</p>
                  <input
                    value={a.name}
                    onChange={(e) => update(idx, "name", 0, e.target.value)}
                    placeholder="Nome do atleta"
                    className="w-full bg-transparent border-b border-white/15 focus:border-gold/60 outline-none font-display text-xl font-bold py-2 placeholder:text-zinc-600 transition-colors"
                    data-testid={`placar-name-${idx}`}
                  />

                  <p className={`font-mono font-bold text-6xl sm:text-7xl text-center my-4 ${accent}`} data-testid={`placar-points-${idx}`}>
                    {a.points}
                  </p>
                  <div className="grid grid-cols-4 gap-2">
                    {[2, 3, 4].map((p) => (
                      <button key={p} type="button" onClick={() => update(idx, "points", p)} className={btnCls} data-testid={`placar-plus${p}-${idx}`}>
                        +{p}
                      </button>
                    ))}
                    <button type="button" onClick={() => update(idx, "points", -1)} className={`${btnCls} text-zinc-500`} data-testid={`placar-minus-point-${idx}`}>
                      -1
                    </button>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div className="border border-white/10 rounded-xl p-3 text-center">
                      <p className="font-mono text-[10px] tracking-widest text-zinc-500">VANTAGENS</p>
                      <p className="font-mono font-bold text-3xl mt-1" data-testid={`placar-adv-${idx}`}>{a.adv}</p>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <button type="button" onClick={() => update(idx, "adv", 1)} className="font-display font-bold rounded-lg border border-white/15 py-2 hover:border-gold/60 hover:text-gold transition-colors" data-testid={`placar-adv-plus-${idx}`}>+</button>
                        <button type="button" onClick={() => update(idx, "adv", -1)} className="font-display font-bold rounded-lg border border-white/15 py-2 text-zinc-500 hover:border-crimson/60 hover:text-crimson transition-colors" data-testid={`placar-adv-minus-${idx}`}>-</button>
                      </div>
                    </div>
                    <div className="border border-white/10 rounded-xl p-3 text-center">
                      <p className="font-mono text-[10px] tracking-widest text-zinc-500">PUNIÇÕES</p>
                      <p className="font-mono font-bold text-3xl mt-1 text-crimson" data-testid={`placar-pen-${idx}`}>{a.pen}</p>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <button type="button" onClick={() => update(idx, "pen", 1)} className="font-display font-bold rounded-lg border border-white/15 py-2 hover:border-gold/60 hover:text-gold transition-colors" data-testid={`placar-pen-plus-${idx}`}>+</button>
                        <button type="button" onClick={() => update(idx, "pen", -1)} className="font-display font-bold rounded-lg border border-white/15 py-2 text-zinc-500 hover:border-crimson/60 hover:text-crimson transition-colors" data-testid={`placar-pen-minus-${idx}`}>-</button>
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => finish(idx)}
                    data-testid={`placar-fin-${idx}`}
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 font-display font-semibold border border-gold/50 text-gold px-6 py-3 rounded-full hover:bg-gold hover:text-obsidian transition-colors"
                  >
                    <Trophy size={16} /> Vitória por Finalização
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {winner !== null && (
          <div className="absolute inset-0 bg-black/85 flex items-center justify-center z-10" data-testid="placar-winner">
            <div className="text-center px-6">
              <Trophy size={48} className="text-gold mx-auto" />
              <p className="font-mono text-xs tracking-[0.3em] text-gold mt-4">VITÓRIA POR FINALIZAÇÃO</p>
              <p className="font-display text-4xl sm:text-5xl font-bold mt-3" data-testid="placar-winner-name">
                {athletes[winner].name || `Atleta ${winner + 1}`}
              </p>
              <p className="font-mono text-sm text-zinc-400 mt-3">
                {athletes[winner].points} pts · {athletes[winner].adv} vant. · tempo restante {fmt(remaining)}
              </p>
              <button
                type="button"
                onClick={() => setWinner(null)}
                data-testid="placar-winner-close"
                className="mt-6 inline-flex items-center gap-2 font-mono text-xs tracking-widest border border-white/20 text-zinc-300 px-6 py-3 rounded-full hover:border-gold/50 hover:text-gold transition-colors"
              >
                <X size={14} /> FECHAR
              </button>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
